import { useCarouselProducts } from "hec/context/carousel-products-context";
import type React from "react";

import type { CIOBrowseProduct } from "~/hec/.server/cio-products.server";

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselSlidePaddingType,
  type CarouselSlidesVisibleTypeDesktop,
  type CarouselSlidesVisibleTypeMobile,
  type CarouselSlidesVisibleTypeTablet,
  ContentButtonNavigation,
  navButtonTypes,
} from "@/components/ui/carousel";
import { useFetchCioProductCategory } from "@/hooks/constructor-io/useFetchCioProductCategory";
import { cn } from "@/lib/utils";
import { isEditing } from "@builder.io/sdk-react";

import { CarouselCta } from "~/hec/components/molecules/carousel-cta/carousel-cta";
import { CarouselHeadline } from "~/hec/components/molecules/carousel-headline/carousel-headline";
import { HecProductTileCms } from "~/hec/components/product/product-tile";
import type { HecProduct } from "~/hec/types/product";
import { cioBrowseToHecProduct } from "~/hec/utils/product-adapters";

import type { AccountFormLabelDataType } from "../account-drawer/accountDrawer.d";

interface ProductCategoryCarouselProps extends AccountFormLabelDataType {
  categoryId: string;
  resultCount: number;
  addedToCartPodId?: string;
  productTileBorderColor?: string;
  showPeek?: boolean;
  showPeekTablet?: boolean;
  showPeekMobile?: boolean;
  slidesVisible?: CarouselSlidesVisibleTypeDesktop;
  slidesVisibleTablet?: CarouselSlidesVisibleTypeTablet;
  slidesVisibleMobile?: CarouselSlidesVisibleTypeMobile;
  slidePadding?: CarouselSlidePaddingType;
  slidePaddingTablet?: CarouselSlidePaddingType;
  slidePaddingMobile?: CarouselSlidePaddingType;
  className?: string;
  ButtonNavigationColor?: string;
  enableHeadline?: boolean;
  headlineText?: string;
  enableNavigationArrows?: boolean;
  showNavigationArrowsMobile?: boolean;
  /** Unset = follows `showNavigationArrowsMobile`. */
  showNavigationArrowsTablet?: boolean;
  showNavigationArrowsDesktop?: boolean;
  ctaText?: string;
  ctaHref?: string;
  ctaExternal?: boolean;
  ctaExternalLink?: string;
  enableBasicView?: boolean;
}

export const ProductCategoryCarousel: React.FC<
  ProductCategoryCarouselProps
> = ({
  categoryId,
  resultCount,
  // Declared in ProductCategoryCarouselProps for Builder.io editor parity
  // but not consumed by this component; destructure out so it doesn't leak
  // to the DOM via the {...props} spread on the wrapping <div>.
  addedToCartPodId,
  productTileBorderColor,
  showPeek,
  showPeekTablet,
  showPeekMobile,
  slidesVisible,
  slidesVisibleTablet,
  slidesVisibleMobile,
  slidePadding,
  slidePaddingTablet,
  slidePaddingMobile,
  className,
  ButtonNavigationColor,
  enableHeadline = false,
  headlineText,
  enableNavigationArrows = false,
  showNavigationArrowsMobile = false,
  showNavigationArrowsTablet = showNavigationArrowsMobile,
  showNavigationArrowsDesktop = false,
  ctaText,
  ctaHref,
  // Declared in ProductCategoryCarouselProps for Builder.io editor parity
  // but not consumed by this component; destructure out so they don't leak
  // to the DOM via the {...props} spread on the wrapping <div>.
  ctaExternal,
  ctaExternalLink,
  enableBasicView = false,
  ...props
}) => {
  const { categoryId: ssrCategoryId, products: ssrProducts } =
    useCarouselProducts();

  const hasSSRProducts =
    ssrProducts.length > 0 && (!ssrCategoryId || ssrCategoryId === categoryId);

  const shouldQuery =
    !hasSSRProducts && typeof categoryId === "string" && categoryId.length > 0;

  const {
    products: cioProducts,
    isLoading,
    hasLoaded,
  } = useFetchCioProductCategory(categoryId, resultCount, shouldQuery);

  const link = ctaHref;

  // SFCC-11820: show skeletons only while the browse is genuinely in flight —
  // from mount until the first response (`!hasLoaded`), OR while a refetch runs
  // (`isLoading`). Previously any `cioProducts.length === 0` forced skeletons,
  // so a category that CIO browse returns EMPTY for (valid category with 0
  // results, or a CIO error the loader swallows into `[]`) rendered skeleton
  // tiles forever — the carousel silently "never loaded" (the reported bug).
  const isFetching = shouldQuery && (isLoading || !hasLoaded);
  const hasProducts = hasSSRProducts || cioProducts.length > 0;

  // Settled with nothing to show (empty/failed browse, or no resolvable
  // category) and not loading → don't render a phantom skeleton carousel. In the
  // Builder editor, render a labelled placeholder so authors can see + position
  // the block and know the category returned nothing; on the storefront,
  // collapse cleanly.
  if (!hasProducts && !isFetching) {
    return isEditing() ? (
      <div
        className={cn("p-[18px] lg:p-[28px]", className)}
        data-testid="productCategoryCarousel-editor-placeholder"
        {...props}
      >
        <div className="flex min-h-[180px] w-full flex-col items-center justify-center gap-1 border-2 border-dashed border-gray-300 bg-gray-50 p-6 text-center text-sm text-gray-500">
          <span className="font-semibold text-gray-700">Product Carousel</span>
          <span>Category: {categoryId ? categoryId : "(none set)"}</span>
          <span>
            No products returned for this category — nothing renders on the live
            storefront.
          </span>
        </div>
      </div>
    ) : null;
  }

  const fetchedProductData: HecProduct[] = hasSSRProducts
    ? ssrProducts.map(cioBrowseToHecProduct)
    : isFetching
      ? Array.from(
          { length: resultCount },
          (_, i) => ({ skeleton: true, id: `skeleton-${i}` }) as HecProduct,
        )
      : cioProducts.map(cioBrowseToHecProduct);

  return (
    <div
      className={cn(
        "p-[18px] lg:p-[28px]",
        { ["pr-0 lg:pr-0"]: showPeek },
        className,
      )}
      {...props}
    >
      <div className="product-carousel-grid-container">
        {enableHeadline && headlineText && (
          <div className="mb-4">
            <CarouselHeadline>{headlineText}</CarouselHeadline>
          </div>
        )}
        <Carousel
          opts={{ loop: true, align: "start" }}
          className="relative flex h-full flex-col justify-center"
          data-testid="productCategoryCarousel"
        >
          <CarouselContent
            className="mx-auto"
            data-testid="productCategoryCarousel-CarouselContent"
          >
            {fetchedProductData?.map((result: HecProduct, i: number) => (
              <CarouselItem
                key={`product-${result.id}-${i}`}
                slidesVisible={slidesVisible || "3.25"}
                slidesVisibleTablet={slidesVisibleTablet}
                slidesVisibleMobile={slidesVisibleMobile || "2.25"}
                showPeek={showPeek}
                showPeekTablet={showPeekTablet}
                showPeekMobile={showPeekMobile}
                slidePadding={slidePadding}
                slidePaddingTablet={slidePaddingTablet}
                slidePaddingMobile={slidePaddingMobile}
                data-testid="productCategoryCarousel-CarouselItem"
              >
                <HecProductTileCms
                  isLoading={result.skeleton}
                  enableRatings={!enableBasicView}
                  product={result}
                  enableAddToCart={false}
                  className="h-full border-2 border-solid border-white"
                  style={
                    productTileBorderColor
                      ? { borderColor: productTileBorderColor }
                      : undefined
                  }
                  // SFCC-12141: real list id/name for select_item item_list_name
                  // (this carousel previously fired GA4_productClick blank).
                  // `headlineText` only counts when the headline actually
                  // renders (enableHeadline) — otherwise the shopper never saw
                  // that name, so fall back to the raw category id.
                  gtmListId={categoryId}
                  gtmListName={(enableHeadline && headlineText) || categoryId}
                  gtmIndex={i + 1}
                />
              </CarouselItem>
            ))}
          </CarouselContent>
          <ContentButtonNavigation
            buttonType={navButtonTypes.FlatButton}
            buttonColor={ButtonNavigationColor}
            className="mt-2 lg:mt-4"
            data-testid="productCategoryCarousel-ContentButtonNavigation"
          />
          {enableNavigationArrows &&
            (showNavigationArrowsMobile || showNavigationArrowsTablet) && (
              <div
                className={cn(
                  "mt-2 flex translate-y-0 gap-2 self-center lg:hidden",
                  !showNavigationArrowsMobile && "max-sm:hidden",
                  !showNavigationArrowsTablet && "sm:max-lg:hidden",
                )}
              >
                <CarouselPrevious
                  className="contents"
                  overlapContent={false}
                  iconAttributes={{ className: "h-6 w-6" }}
                  data-testid="productCategoryCarousel-CarouselPrevious"
                />
                <CarouselNext
                  className="contents"
                  overlapContent={false}
                  iconAttributes={{ className: "h-6 w-6" }}
                  data-testid="productCategoryCarousel-CarouselNext"
                />
              </div>
            )}
          {enableNavigationArrows && showNavigationArrowsDesktop && (
            <div className="mt-2 hidden translate-y-0 gap-2 self-end pr-4 lg:flex">
              <CarouselPrevious
                className="contents"
                overlapContent={false}
                iconAttributes={{ className: "h-6 w-6" }}
                data-testid="productCategoryCarousel-CarouselPrevious-Desktop"
              />
              <CarouselNext
                className="contents"
                overlapContent={false}
                iconAttributes={{ className: "h-6 w-6" }}
                data-testid="productCategoryCarousel-CarouselNext-Desktop"
              />
            </div>
          )}
          {ctaText && (
            <CarouselCta
              text={ctaText}
              href={link}
              className={cn({ "lg:mt-[0px]": showNavigationArrowsDesktop })}
              data-testid="productCategoryCarousel-cta-button"
            />
          )}
        </Carousel>
      </div>
    </div>
  );
};
